/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import debounce from 'lodash.debounce';
import { Input } from '@/components/ui/input';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Search as SearchIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Suggestions from '@/components/search/suggestions';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { getSessionJSONItem, setSessionJSONItem, clearSessionJSONItem } from '@/lib/utils';
import { resolveSearchViewport, type ManualCategoriesStatus } from '@/components/search/default-search-viewport';
import { useHeaderPanel } from '@/components/header/use-header-panel';
import UiTargetSmokeTest_GenericMarker from '@/extensions/ui-target-smoke-test/components/generic-marker';
import { RemoveScroll } from 'react-remove-scroll';
const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';
const RECENT_SEARCH_MIN_LENGTH = 3;
interface SearchBarProps {
  /** Lifecycle of merchant-managed search suggestion categories (resolved above remount boundary). */
  categoriesStatus?: ManualCategoriesStatus;
  /** Markup from the search-suggestion-categories content asset. */
  categoriesMarkup?: string;
}
export default function SearchBar({
  categoriesStatus = 'absent',
  categoriesMarkup
}: SearchBarProps): ReactElement {
  const {
    t
  } = useTranslation('header');
  const navigate = useNavigate();
  const config = useConfig();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchWidgetRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [recentSearchesEpoch, setRecentSearchesEpoch] = useState(0);
  const queryRef = useRef(query);
  const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const recentSearches = useMemo(() => {
    void recentSearchesEpoch;
    return getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
  }, [recentSearchesEpoch]);
  const {
    data: suggestions,
    refetch
  } = useSearchSuggestions({
    q: query,
    expand: ['images', 'prices'],
    includeEinsteinSuggestedPhrases: config.pages.search.enableEinsteinSuggestedPhrases,
    enabled: query.trim().length >= RECENT_SEARCH_MIN_LENGTH
  });
  const transformedSuggestions = useTransformSearchSuggestions(suggestions);
  useEffect(() => {
    queryRef.current = query;
    refetchRef.current = refetch;
  }, [query, refetch]);
  const saveRecentSearch = useCallback((searchText: string) => {
    let searches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
    searches = searches.filter(savedSearchTerm => {
      return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
    });
    searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);
    setSessionJSONItem(RECENT_SEARCH_KEY, searches);
    setRecentSearchesEpoch(epoch => epoch + 1);
  }, []);
  const debouncedRefetch = useMemo(() => {
    return debounce(() => {
      const currentQuery = queryRef.current;
      if (currentQuery.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
        void refetchRef.current();
      }
    }, config.pages.search.suggestionsDebounce);
  }, [config.pages.search.suggestionsDebounce]);
  useEffect(() => {
    if (query.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
      debouncedRefetch();
    } else {
      debouncedRefetch.cancel();
    }
    return () => {
      debouncedRefetch.cancel();
    };
  }, [query, debouncedRefetch]);
  const viewport = useMemo(() => {
    return resolveSearchViewport({
      query,
      recentSearchCount: recentSearches.length,
      categoriesStatus,
      apiSuggestions: transformedSuggestions,
      isFocused,
      dismissed,
      minQueryLength: RECENT_SEARCH_MIN_LENGTH
    });
  }, [query, recentSearches.length, categoriesStatus, transformedSuggestions, isFocused, dismissed]);
  const isViewportOpen = viewport !== 'closed';
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    // Clearing dismissed lets the pure resolver reopen when content becomes eligible again.
    setDismissed(false);
  }, []);
  const handleSearchFocusIn = useCallback(() => {
    setIsFocused(true);
    setDismissed(false);
  }, []);

  // Close when focus moves to another page element outside search. Keep the panel open
  // when relatedTarget is null (DevTools, window blur) so the viewport stays inspectable.
  const handleSearchFocusOut = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget === null) {
      return;
    }
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setIsFocused(false);
  }, []);
  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (inputRef.current?.value?.trim()) {
      const searchQuery = inputRef.current.value.trim();
      saveRecentSearch(searchQuery);
      setDismissed(true);
      void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
        state: {
          query: searchQuery
        }
      });
    }
  }, [navigate, saveRecentSearch]);
  const clearSearch = useCallback(() => {
    inputRef.current?.blur();
    setDismissed(true);
    setQuery('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    debouncedRefetch.cancel();
  }, [debouncedRefetch]);
  const closeAndNavigate = useCallback((link: string) => {
    clearSearch();
    if (link) {
      void navigate(link);
    }
  }, [navigate, clearSearch]);
  const clearRecentSearches = useCallback(() => {
    clearSessionJSONItem(RECENT_SEARCH_KEY);
    setRecentSearchesEpoch(epoch => epoch + 1);
  }, []);
  const dismissSuggestions = useCallback(() => {
    setDismissed(true);
    inputRef.current?.blur();
  }, []);
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') {
      return;
    }
    if (query.length > 0) {
      e.preventDefault();
      clearSearch();
    } else if (isViewportOpen) {
      e.preventDefault();
      dismissSuggestions();
    }
  }, [query, clearSearch, isViewportOpen, dismissSuggestions]);
  const headerPanel = useHeaderPanel();
  const setPanelOpen = headerPanel?.setPanelOpen;
  const registerPanelCloseHandler = headerPanel?.registerPanelCloseHandler;
  useEffect(() => {
    if (!isViewportOpen) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      // The widget contains both the input and the non-portaled suggestions panel.
      // Page-content clicks are handled by the shared HeaderOverlay; this covers
      // header chrome (logo, icons) that sits above the backdrop.
      if (searchWidgetRef.current?.contains(target)) {
        return;
      }
      dismissSuggestions();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isViewportOpen, dismissSuggestions]);
  useEffect(() => {
    setPanelOpen?.('search', isViewportOpen);
  }, [isViewportOpen, setPanelOpen]);
  useEffect(() => {
    registerPanelCloseHandler?.('search', dismissSuggestions);
    return () => {
      registerPanelCloseHandler?.('search', null);
      setPanelOpen?.('search', false);
    };
  }, [registerPanelCloseHandler, dismissSuggestions, setPanelOpen]);
  return <UiTargetSmokeTest_GenericMarker>
            <div ref={searchWidgetRef} onFocusCapture={handleSearchFocusIn} onBlurCapture={handleSearchFocusOut}>
                <Popover open={isViewportOpen}>
                    <form onSubmit={handleSubmit} className="relative z-10">
                        <div className="relative">
                            <PopoverTrigger asChild>
                                <Input ref={inputRef} type="text" placeholder={t('searchPlaceholder')} className="h-10 w-full !rounded-full border-0 bg-brand-gray-300 pl-12 pr-10 text-base font-normal text-foreground placeholder:text-header-search-placeholder hover:bg-header-search-background focus:bg-header-search-background shadow-none focus-visible:border-0 focus-visible:ring-0 lg:text-sm" onChange={handleInputChange} onKeyDown={handleKeyDown} aria-label={t('searchPlaceholder')} aria-autocomplete="list" aria-expanded={isViewportOpen} aria-haspopup="listbox" role="combobox" data-testid="header-search-input" />
                            </PopoverTrigger>
                            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-header-foreground" />
                            {(query.length > 0 || isViewportOpen) && <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-header-foreground" onMouseDown={e => {
              e.preventDefault();
              if (query.length > 0) {
                clearSearch();
              } else {
                dismissSuggestions();
              }
            }} aria-label={t(query.length > 0 ? 'clearSearch' : 'closeSearchSuggestions')} data-testid="header-search-clear">
                                    <X className="size-5" aria-hidden />
                                </button>}
                        </div>
                    </form>
                    {/* Note: Using a fixed div instead of PopoverContent because the search
                     suggestions panel is designed to span the full page width while the trigger is input with limited width in the header.
                     Using w-screen on Radix PopoverContent will include the scrollbar width, while the rest of page does not aware of scrollbar width
                     this causing the content in Popover to completely miss aligned with the rest of the layout despite using the same section gutter class.
                     Therefore, we go with traditional div to get control over styling for search result area*/}
                    {viewport !== 'closed' && <RemoveScroll allowPinchZoom forwardProps>
                            <div data-slot="search-suggestions-viewport" className="fixed left-0 right-0 z-[60] max-h-[min(90vh,48rem)] overscroll-contain overflow-y-auto bg-header-menu-background text-header-menu-foreground" style={{
            top: 'calc(var(--header-height, 0px) + var(--ticker-height, 0px))'
          }} role="listbox" aria-label={t('searchSuggestions')} onMouseDown={event => {
            // Keep focus on the input so the panel stays open while interacting with suggestions.
            event.preventDefault();
          }}>
                                <Suggestions searchSuggestions={transformedSuggestions} recentSearches={recentSearches} closeAndNavigate={closeAndNavigate} clearRecentSearches={clearRecentSearches} viewport={viewport} categoriesStatus={categoriesStatus} categoriesMarkup={categoriesMarkup} />
                            </div>
                        </RemoveScroll>}
                </Popover>
            </div>
        </UiTargetSmokeTest_GenericMarker>;
}